import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader, Copy, RefreshCw, Check, Calendar as CalendarIcon, MessageCircle, AlertTriangle, ListOrdered } from 'lucide-react';
import { toast } from 'sonner';
import { OrganisationBTDialog } from '@/components/organisation/OrganisationBTDialog';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Helpers date locale (sans dépendance externe)
const ymdLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const addDays = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
};

/**
 * 🆕 2026-05-07 — Message horaire à coller dans Messenger.
 * Génère un message friendly listant qui travaille où, regroupé par équipe,
 * basé sur les BT cédulés à la date choisie (par défaut : demain).
 */
export function MessageHoraireDialog({ open, onClose, defaultDate = null }) {
  const today = new Date();
  const tomorrow = addDays(today, 1);

  const [selectedDate, setSelectedDate] = useState(defaultDate || ymdLocal(tomorrow));
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState({ nb_equipes: 0, nb_bts: 0 });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // 🆕 [Phase 51] Blocage : si 409 avec employes_sans_ordre, on affiche
  // le message d'erreur officiel + bouton d'accès direct au dialog organisation.
  const [blockedReason, setBlockedReason] = useState(null);  // {message, employes_sans_ordre: [...]}
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);

  const generer = useCallback(async (dateStr) => {
    setLoading(true);
    setCopied(false);
    setBlockedReason(null);
    try {
      const res = await axios.get(`${API_URL}/api/production-calendar/message-horaire`, {
        params: { date: dateStr },
      });
      setMessage(res.data?.message || '');
      setStats({
        nb_equipes: res.data?.nb_equipes || 0,
        nb_bts: res.data?.nb_bts || 0,
      });
    } catch (err) {
      // 🆕 [Phase 51] Si l'API renvoie 409 avec employes_sans_ordre, on bloque
      // proprement et on propose d'ouvrir le dialog Organisation.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && detail.code === 'ordre_bt_manquant') {
        setBlockedReason(detail);
        setMessage('');
      } else {
        console.error('Erreur génération message:', err);
        toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la génération');
        setMessage('');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Génère automatiquement à l'ouverture / changement de date
  useEffect(() => {
    if (open) {
      generer(selectedDate);
    }
  }, [open, selectedDate, generer]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('✅ Message copié dans le presse-papier !');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback : sélectionner le textarea pour copie manuelle
      toast.error('Impossible de copier automatiquement. Sélectionnez le texte et faites Cmd+C / Ctrl+C.');
    }
  };

  // Quick selectors
  const todayStr = ymdLocal(today);
  const tomorrowStr = ymdLocal(tomorrow);
  const dayAfterStr = ymdLocal(addDays(today, 2));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-message-horaire">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            Message horaire pour Messenger
          </DialogTitle>
          <DialogDescription>
            Génère un message friendly qui résume la journée par équipe, prêt à coller dans le groupe Messenger.
          </DialogDescription>
        </DialogHeader>

        {/* Sélecteur rapide de date */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <CalendarIcon className="w-3.5 h-3.5" /> Date :
          </span>
          <Button
            size="sm"
            variant={selectedDate === todayStr ? 'default' : 'outline'}
            onClick={() => setSelectedDate(todayStr)}
            data-testid="btn-msg-aujourdhui"
            className="h-7 text-xs"
          >
            Aujourd'hui
          </Button>
          <Button
            size="sm"
            variant={selectedDate === tomorrowStr ? 'default' : 'outline'}
            onClick={() => setSelectedDate(tomorrowStr)}
            data-testid="btn-msg-demain"
            className="h-7 text-xs"
          >
            Demain
          </Button>
          <Button
            size="sm"
            variant={selectedDate === dayAfterStr ? 'default' : 'outline'}
            onClick={() => setSelectedDate(dayAfterStr)}
            data-testid="btn-msg-apres-demain"
            className="h-7 text-xs"
          >
            Après-demain
          </Button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="h-7 text-xs border rounded-md px-2"
            data-testid="input-msg-date-custom"
          />
        </div>

        {/* Stats rapides */}
        {!loading && message && (
          <div className="text-[11px] text-slate-500 flex items-center gap-3">
            <span>📋 {stats.nb_equipes} équipe{stats.nb_equipes > 1 ? 's' : ''}</span>
            <span>🔧 {stats.nb_bts} BT{stats.nb_bts > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Zone éditable */}
        <div className="relative">
          {loading ? (
            <div className="border rounded-md p-8 flex items-center justify-center bg-slate-50">
              <Loader className="w-5 h-5 animate-spin text-emerald-600 mr-2" />
              <span className="text-sm text-slate-500">Génération en cours…</span>
            </div>
          ) : blockedReason ? (
            /* 🆕 [Phase 51 + 🛡️ Phase 57 v11] Diagnostic verbeux structuré :
               affiche tous les `blockers` retournés par le backend avec une
               action concrète pour chacun. Conserve la rétro-compat avec
               l'ancien format `employes_sans_ordre`. */
            <div
              className="border-2 border-red-300 bg-red-50 rounded-md p-4 space-y-3"
              data-testid="msg-blocked-ordre-bt"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800 font-bold">
                    🚫 Impossible de générer le message d'horaire
                  </p>
                  <p className="text-sm text-red-700 mt-1">{blockedReason.message}</p>
                </div>
              </div>

              {/* 🛡️ Phase 57 — Liste structurée des blockers détectés */}
              {Array.isArray(blockedReason.blockers) && blockedReason.blockers.length > 0 ? (
                <div className="space-y-3" data-testid="msg-blockers-list">
                  {blockedReason.blockers.map((b, idx) => (
                    <div
                      key={`${b.type}-${idx}`}
                      className={`rounded-md p-3 border ${
                        b.severity === 'blocking'
                          ? 'bg-red-100 border-red-300'
                          : 'bg-amber-50 border-amber-300'
                      }`}
                      data-testid={`msg-blocker-${b.type}`}
                    >
                      <p className={`text-sm font-semibold ${
                        b.severity === 'blocking' ? 'text-red-800' : 'text-amber-800'
                      }`}>
                        {b.severity === 'blocking' ? '🔴' : '🟠'} {b.label}
                      </p>
                      {b.type === 'employes_en_double' && (
                        <ul className="list-disc list-inside text-sm text-red-700 mt-1 space-y-0.5">
                          {(b.details || []).map(d => (
                            <li key={d.employee_id} data-testid={`msg-blocked-emp-${d.employee_id}`}>
                              <strong>{d.nom}</strong> → {(d.bt_clients || []).join(' ET ')}
                            </li>
                          ))}
                        </ul>
                      )}
                      {b.type === 'bt_sans_chef' && (
                        <ul className="list-disc list-inside text-sm text-amber-700 mt-1 space-y-0.5">
                          {(b.details || []).map(d => (
                            <li key={d.bt_id} data-testid={`msg-bt-sans-chef-${d.bt_id}`}>
                              <strong>{d.client_nom}</strong>
                              {d.numero_bt ? ` (BT ${d.numero_bt})` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                      {b.action === 'organiser_ordre' && (
                        <Button
                          size="sm"
                          className="mt-2 bg-red-600 hover:bg-red-700"
                          onClick={() => setOrgDialogOpen(true)}
                          data-testid={`btn-action-${b.action}`}
                        >
                          <ListOrdered className="w-4 h-4 mr-1" />
                          Organiser l'ordre des journées
                        </Button>
                      )}
                      {b.action === 'assigner_chef' && (
                        <p className="text-xs text-amber-700 italic mt-2">
                          Ouvrez chaque BT depuis le calendrier et sélectionnez
                          un chef d'équipe (ou un chef-du-jour) pour cette date.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                /* Fallback legacy : ancien format employes_sans_ordre */
                <>
                  <div className="text-sm text-red-700 space-y-1">
                    <p className="font-semibold">Employés concernés :</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {(blockedReason.employes_sans_ordre || []).map(e => (
                        <li key={e.id} data-testid={`msg-blocked-emp-${e.id}`}>
                          <strong>{e.nom}</strong> : {(e.bt_clients || []).join(' / ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-sm text-red-700 italic">
                    Veuillez aller dans la section « Organisation des bons de travail » afin de définir l&apos;ordre des chantiers avant de générer le message d&apos;horaire.
                  </p>
                  <Button
                    size="sm"
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => setOrgDialogOpen(true)}
                    data-testid="btn-open-org-bt"
                  >
                    <ListOrdered className="w-4 h-4 mr-1" />
                    Ouvrir Organisation des BT
                  </Button>
                </>
              )}
            </div>
          ) : (
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={14}
              className="font-mono text-xs leading-relaxed resize-y"
              data-testid="textarea-message-horaire"
              placeholder="Aucun message généré"
            />
          )}
        </div>

        <p className="text-[11px] text-slate-400">
          💡 Tu peux éditer le message avant de le copier — variations aléatoires d'emojis et de salutations à chaque génération pour garder un ton naturel.
        </p>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => generer(selectedDate)}
            disabled={loading}
            data-testid="btn-msg-regenerer"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Autre variante
          </Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={handleCopy}
            disabled={loading || !message}
            data-testid="btn-msg-copier"
          >
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? 'Copié !' : 'Copier dans Messenger'}
          </Button>
        </div>
      </DialogContent>

      {/* 🆕 [Phase 51] Dialog Organisation des BT — accessible depuis le blocage */}
      <OrganisationBTDialog
        open={orgDialogOpen}
        onClose={() => setOrgDialogOpen(false)}
        date={selectedDate}
        onSaved={() => generer(selectedDate)}
      />
    </Dialog>
  );
}
